import { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { useGlobalContext } from "../../GlobalContext";
import {
  Truck,
  PieChart as PieIcon,
  BadgeCheck,
  XCircle,
  Loader,
  AlertCircle,
  Send,
  Download,
  BarChart2,
} from "lucide-react";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import Logo from "../../Images/Packky.svg";
import LoadingScreen from "../Utils/LoadingScreen";
import { useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import TablePagination from "../Utils/TablePagination";
import useReports from "../../Sevices/useReports";
import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

const ReportsDashboard = () => {
  const { accessToken, AdminId, admin } = useGlobalContext();
  const navigate = useNavigate();

  const { fetchPackkyData, loading, loadingDrivers } =
    useReports();

 const {
    drivers,
    fetchDrivers,
  } = useDriverAllApi();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [restaurantData, setRestaurantData] = useState([]);
  const [driverTaskAnalytics, setDriverTaskAnalytics] = useState([]);
  const [taskStatusAnalytics, setTaskStatusAnalytics] = useState([]);
  const [activeTab, setActiveTab] = useState("packky");
  const [search, setSearch] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const renderPagination = () => {
    const totalCount = filteredDrivers?.length || 0;

    if (totalCount <= driversPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={driversPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };

  const handlePackkyExport = () => {
    if (
      taskStatusAnalytics.length === 0 &&
      restaurantData.length === 0 &&
      driverTaskAnalytics.length === 0
    ) {
      alert("No Packky report data available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: taskStatusAnalytics,
      filePrefix: `Packky_Task_Status_${fromDate}_to_${toDate}`,
      sheetName: "Task Status",
    });

    exportAnyJsonToExcel({
      data: restaurantData,
      filePrefix: `Packky_Restaurant_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Restaurant Tasks",
    });

    exportAnyJsonToExcel({
      data: driverTaskAnalytics,
      filePrefix: `Packky_Driver_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Driver Tasks",
    });
  };

  const filteredDrivers = drivers?.filter((driver) => {
    if (!search) return true;

    const q = search.toLowerCase();

    return (
      driver.fullName?.toLowerCase().includes(q) ||
      driver.first_name?.toLowerCase().includes(q) ||
      driver.last_name?.toLowerCase().includes(q) ||
      driver.username?.toLowerCase().includes(q) ||
      driver.driverId?.toLowerCase().includes(q) ||
      driver.email?.toLowerCase().includes(q) ||
      driver.phone?.toLowerCase().includes(q) ||
      driver.teamName?.toLowerCase().includes(q)
    );
  });

  const driversPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLast = currentPage * driversPerPage;
  const indexOfFirst = indexOfLast - driversPerPage;
  const totalPages = Math.ceil(filteredDrivers?.length / driversPerPage);
  const currentDrivers = filteredDrivers?.slice(indexOfFirst, indexOfLast);

  const getStatusConfig = (status) => {
    const statusLower = status.toLowerCase();
    if (
      ["completed", "successful", "delivered", "arrived"].includes(statusLower)
    ) {
      return { Icon: BadgeCheck, color: "#059669" };
    }
    if (
      ["failed", "cancelled", "canceled", "error", "rejected"].includes(
        statusLower,
      )
    ) {
      return { Icon: XCircle, color: "#dc2626" };
    }
    if (["food is ready"].includes(statusLower)) {
      return { Icon: Send, color: "#14b8a6" };
    }
    if (["declined", "unassigned"].includes(statusLower)) {
      return { Icon: XCircle, color: "#a855f7" };
    }
    if (["pending", "inprogress", "assigned"].includes(statusLower)) {
      return { Icon: Loader, color: "#2563eb" };
    }
    if (["sent", "dispatched"].includes(statusLower)) {
      return { Icon: Send, color: "#f59e0b" };
    }
    return { Icon: AlertCircle, color: "#6b7280" };
  };

useEffect(() => {
    fetchDrivers();  
}, []);

useEffect(() => {
  if (admin?.userId && accessToken) {
    fetchPackkyData(

       fromDate,
       toDate,
      setRestaurantData,
      setDriverTaskAnalytics,
      setTaskStatusAnalytics,
    );
  }
}, [admin, accessToken]);

  const driverBarChartData = {
    labels: driverTaskAnalytics.map((d) => d.driverName),
    datasets: [
      {
        label: "Tasks",
        data: driverTaskAnalytics.map((d) => d.taskCount),
        backgroundColor: driverTaskAnalytics.map(
          (_, i) =>
            ["#2563eb", "#f59e0b", "#059669", "#dc2626", "#8b5cf6", "#22c55e"][
              i % 5
            ],
        ),
        borderRadius: 6,
      },
    ],
  };

  const restaurantPieChartData = {
    labels: restaurantData.map((r) => r.shopName),
    datasets: [
      {
        data: restaurantData.map((r) => r.taskCount),
        backgroundColor: [
          "#2563eb",
          "#f59e0b",
          "#059669",
          "#22c55e",
          "#dc2626",
          "#8b5cf6",
        ],
      },
    ],
  };

  const handleOpen = () => {
    if (!selectedDriver || !pendingAction) return;

    const driverId = selectedDriver.driverId;
    const username = encodeURIComponent(
      selectedDriver.username || selectedDriver.fullName,
    );

    if (pendingAction === "analytics") {
      navigate(`/analytics/${driverId}/${username}`);
    } else if (pendingAction === "report") {
      navigate(`/reports/${driverId}/${username}`);
    }

    setConfirmOpen(false);
    setPendingAction(null);
    setSelectedDriver(null);
  };

  const renderDriversTable = () => (
    <div className="overflow-x-auto border rounded-md shadow-sm">
      <table className="min-w-full bg-white">
        <thead>
          <tr className="bg-orange-400 text-left text-sm font-semibold text-white">
            <th className="px-4 py-2 text-center">S/N</th>
            <th className="px-4 py-2 text-center">Driver ID</th>
            <th className="px-4 py-2 text-center">User Name</th>
            <th className="px-4 py-2 text-center">Name</th>
            <th className="px-4 py-2 text-center">Email</th>
            <th className="px-4 py-2 text-center">Phone</th>
            <th className="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loadingDrivers ? (
            <tr>
              <td colSpan="7" className="px-4 py-8 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Loader className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="text-gray-600">Loading drivers...</span>
                </div>
              </td>
            </tr>
          ) : currentDrivers?.length === 0 ? (
            <tr>
              <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                No drivers found
              </td>
            </tr>
          ) : (
            currentDrivers?.map((driver, index) => (
              <tr key={driver.id} className="text-sm border-t">
                <td className="px-4 py-2 text-center">
                  {indexOfFirst + index + 1}
                </td>
                <td className="px-4 py-2 text-center">{driver.driverId}</td>
                <td className="px-4 py-2 text-center">{driver.username}</td>
                <td className="px-4 py-2 font-medium text-center">
                  {driver.fullName}
                </td>
                <td className="px-4 py-2 text-center">{driver.email}</td>
                <td className="px-4 py-2 text-center">{driver.phone}</td>
                <td className="px-4 py-2 text-center">
                  <div className="flex gap-4 justify-center items-center">
                    <button
                      className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs hover:bg-gray-50"
                      onClick={() => {
                        setSelectedDriver(driver);
                        setPendingAction("analytics");
                        setConfirmOpen(true);
                      }}
                    >
                      <BarChart2 size={14} /> Analytics
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="p-6 min-h-screen space-y-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="h-10" />
            <p className="text-2xl font-bold ps-3">
              {activeTab === "driver" ? "Driver Report" : "Report"}
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("packky")}
                  className={`py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "packky"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Packky Reports
                </button>
                <button
                  onClick={() => setActiveTab("driver")}
                  className={`py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "driver"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Driver Reports
                </button>
              </nav>
            </div>

            {loading ? (
              <LoadingScreen />
            ) : (
              <>
                {activeTab === "driver" && (
                  <>
                    <div className="flex flex-wrap md:flex-nowrap gap-3 mb-4 items-end">
                      <input
                        type="text"
                        placeholder="Search by name, code, email, phone..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="px-3 py-2 border rounded text-sm w-full md:w-64"
                      />

                      {/* <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="border p-2 rounded text-sm w-full md:w-44"
                      />

                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="border p-2 rounded text-sm w-full md:w-44"
                      />

                      <button
                        onClick={() => {
                          fetchPackkyData(
                            fromDate,
                            toDate,
                            setRestaurantData,
                            setDriverTaskAnalytics,
                            setTaskStatusAnalytics,
                          );
                          fetchDrivers();
                          setCurrentPage(1);
                        }}
                        className="min-w-[96px] h-10 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center justify-center"
                      >
                        Apply
                      </button> */}

                      <div className="flex gap-2 ml-auto">
                        <button
                          className="min-w-[96px] h-10 px-3 py-1 bg-green-500 text-white rounded"
                          onClick={() => {
                            fetchPackkyData(
                              fromDate,
                              toDate,
                              setRestaurantData,
                              setDriverTaskAnalytics,
                              setTaskStatusAnalytics,
                            );
                            fetchDrivers();
                          }}
                        >
                          Refresh
                        </button>
                      </div>
                    </div>

                    {renderDriversTable()}
                    {renderPagination()}
                  </>
                )}

                {activeTab === "packky" && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {taskStatusAnalytics.map((status) => {
                        const { Icon, color } = getStatusConfig(
                          status.taskStatus,
                        );
                        return (
                          <div
                            key={status.id}
                            className="group relative bg-white p-3 shadow rounded text-center border-l-4 transition-all duration-300 hover:scale-[1.02]"
                            style={{ borderColor: color }}
                          >
                            <div
                              className="absolute inset-0 rounded z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              style={{ backgroundColor: color }}
                            ></div>

                            <div className="relative z-10">
                              <div className="flex items-center justify-center mb-2">
                                <Icon className="w-6 h-6 mr-2 text-gray-600 transition-colors duration-300 group-hover:text-white" />
                                <p className="text-lg capitalize font-medium text-gray-600 transition-colors duration-300 group-hover:text-white">
                                  {status.taskStatus}
                                </p>
                              </div>
                              <p className="text-xl font-bold text-gray-800 transition-colors duration-300 group-hover:text-white">
                                {status.taskCount}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <hr />

                    {/* Filters + Pie Chart */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                      <div className="space-y-3 border rounded bg-white p-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            From Date
                          </label>
                          <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="p-2 border rounded w-full text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            To Date
                          </label>
                          <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="p-2 border rounded w-full text-sm"
                          />
                        </div>
                        <button
                          onClick={() =>
                            fetchPackkyData(
                              fromDate,
                              toDate,
                              setRestaurantData,
                              setDriverTaskAnalytics,
                              setTaskStatusAnalytics,
                            )
                          }
                          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded w-full text-sm"
                        >
                          Apply Filter
                        </button>
                        {activeTab === "packky" && (
                          <div className="flex justify-end mb-4">
                            <button
                              className="w-full h-10 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center justify-center gap-2"
                              onClick={handlePackkyExport}
                            >
                              <Download size={16} />
                              <span>Export</span>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="col-span-2 bg-white p-4 shadow rounded">
                        <h2 className="text-xl mb-2 flex items-center gap-2">
                          <PieIcon className="w-4 h-4 text-blue-600" />{" "}
                          Restaurant Orders Distribution
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2 overflow-y-auto max-h-64 pr-2">
                            {restaurantData.map((r, i) => (
                              <div
                                key={r.id}
                                className="flex items-center justify-between border rounded px-3 py-2 text-sm"
                                style={{
                                  borderLeft: `4px solid ${
                                    [
                                      "#2563eb",
                                      "#f59e0b",
                                      "#059669",
                                      "#dc2626",
                                      "#8b5cf6",
                                    ][i % 5]
                                  }`,
                                }}
                              >
                                <span className="font-medium">
                                  {r.shopName}
                                </span>
                                <span className="font-semibold text-gray-700">
                                  {r.taskCount}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="h-64 border-l">
                            <Pie
                              data={restaurantPieChartData}
                              options={{ maintainAspectRatio: false }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />

                    {/* Driver Bar Graph */}
                    <div className="bg-white p-4 shadow rounded">
                      <h2 className="text-xl mb-4 flex items-center gap-2">
                        <Truck className="w-5 h-5 text-green-600" /> Driver-wise
                        Task Count
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="h-64 border-r pr-3">
                          <Bar
                            data={driverBarChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              animation: {
                                duration: 800,
                                easing: "easeOutBounce",
                              },
                              plugins: { legend: { display: false } },
                            }}
                          />
                        </div>
                        <div className="overflow-y-auto max-h-64 pr-2 space-y-2">
                          <p className="text-xl">Driver</p>
                          {driverTaskAnalytics.map((d, i) => (
                            <div
                              key={d.id}
                              className="flex items-center justify-between border rounded px-3 py-2 text-sm"
                              style={{
                                borderLeft: `4px solid ${
                                  [
                                    "#2563eb",
                                    "#f59e0b",
                                    "#059669",
                                    "#dc2626",
                                    "#8b5cf6",
                                  ][i % 5]
                                }`,
                              }}
                            >
                              <span className="font-medium">
                                {d.driverName}
                              </span>
                              <span className="font-semibold text-gray-700">
                                {d.taskCount}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-sm p-5">
            <h3 className="text-lg font-semibold mb-2">Open Driver Report</h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to open{" "}
              <span className="font-semibold capitalize">
                {pendingAction === "analytics" ? "analytics" : "report"}
              </span>{" "}
              for{" "}
              <span className="font-medium">{selectedDriver?.fullName}</span>?
            </p>

            <div className="flex justify-end gap-3">
              {/* Cancel */}
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setSelectedDriver(null);
                }}
                className="px-4 py-2 text-sm rounded border hover:bg-gray-50"
              >
                Cancel
              </button>

              {/* Open */}
              <button
                onClick={handleOpen}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsDashboard;
